<template>
  <div>
    <b-card
      id="full-ens-form"
    >
      <b-card-title>
        <div class="float-left">
          <h4>{{ title }}</h4>
        </div>
        <div class="float-right mb-1">
          <!--This span is required to show tooltip on disabled button. -->
          <span
            v-b-tooltip.hover.top.v-danger="getTooltipMsg"
          >
            <b-button
              id="submit-full-ens"
              variant="primary"
              :disabled="manifestHasIssues || requestPending || hasNoNotSentParcels || ensSubmitted"
              @click="sendEns(ensMessageType, ensRequestType)"
            >
              {{ ensSubmitButtonLabel }}
              <b-spinner
                v-if="requestPending"
                small
              />
            </b-button>
          </span>
        </div>
        <b-form-checkbox
          id="show-ens-statuses"
          v-model="showAllENSStatuses"
          switch
          class="float-right mx-2"
        >Show all ENS statuses</b-form-checkbox>
      </b-card-title>
      <b-table
        :fields="fields"
        :items="ENSItems"
        thead-class="d-none"
      >
        <template #cell(name)="data">
          <router-link
            v-if="data.item.name !== 'Not sent to customs'"
            :to="data.item.link"
          >
            <strong :class="data.item.class">{{ data.item.name }}</strong>
          </router-link>
          <strong
            v-else
            :class="data.item.class"
          >{{ data.item.name }}</strong>
        </template>

        <template #cell(count)="data">
          <router-link
            v-if="data.item.name !== 'Not sent to customs'"
            :to="data.item.link"
          >
            <strong :class="data.item.class">{{ data.item.count }}</strong>
          </router-link>
          <strong
            v-else
            :class="data.item.class"
          >{{ data.item.count }}</strong>
        </template>

      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BFormCheckbox,
  BTable,
  BCardTitle,
  BSpinner,
  BButton,
  VBTooltip,
} from 'bootstrap-vue'
import handleError from '@/views/components/errorHandler'
import { EventBus } from '@core/utils/utils'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BSpinner,
    BCardTitle,
    BFormCheckbox,
    BCard,
    BTable,
    BButton,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    shipment: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      requestPending: false,
      ensSubmitted: false,
      showAllENSStatuses: false,
      declarations: this.shipment.ens_declarations || [],
      title: 'ENS submission process',
      showMarkDoneConfirm: false,
      fields: [
        'name',
        {
          key: 'count',
          tdClass: 'text-right',
        },
      ],
    }
  },
  computed: {
    transportMode() {
      // Determine transport mode from shipment (as defined in ShipmentForm.vue)
      return this.shipment.default_form || 'airplane'
    },
    ensMessageType() {
      // F26 for airplane (pre-arrival), F50 for road, F51 for rail
      if (this.transportMode === 'road') return 'F50'
      if (this.transportMode === 'rail') return 'F51'
      return 'F26'
    },
    ensRequestType() {
      // 'road' for road, 'rail' for rail, 'pre-arrival' for airplane
      if (this.transportMode === 'road') return 'road'
      if (this.transportMode === 'rail') return 'rail'
      return 'pre-arrival'
    },
    ensSubmitButtonLabel() {
      // Dynamic button label based on transport mode
      return this.transportMode === 'road' || this.transportMode === 'rail'
        ? 'Submit ENS'
        : 'Submit ENS house-filings'
    },
    manifestHasIssues() {
      const { statistics } = this.$attrs
      return statistics?.prohibited?.h7 > 0 || statistics?.reducedVat?.h7 > 0 || statistics?.missingHsCodes?.h7 > 0
    },
    ENSItems() {
      const { statistics } = this.$attrs
      let statsRows = [
        {
          name: 'Not sent to customs',
          link: `/shipments/${this.shipment.id}/declarations/not-sent-ens`,
          class: 'text-secondary',
          count: statistics?.notSent?.ens ?? 0,
        },
        {
          name: 'Preparing to send',
          link: `/shipments/${this.shipment.id}/declarations/preparing-to-send-ens`,
          class: 'text-secondary',
          count: statistics?.preparingToSend?.ens ?? 0,
        },
        {
          name: 'Waiting for customs',
          link: `/shipments/${this.shipment.id}/declarations/waiting-customs-ens`,
          class: 'text-secondary',
          count: statistics?.waitingForCustoms?.ens ?? 0,
        },
        {
          name: 'Needs attention',
          link: `/shipments/${this.shipment.id}/declarations/needs-attention-ens`,
          class: 'text-warning',
          count: statistics?.needsAttention?.ens ?? 0,
        },
        {
          name: 'Registered',
          link: `/shipments/${this.shipment.id}/declarations/registered-ens`,
          class: 'text-success',
          count: statistics?.registered?.ens ?? 0,
        },
        {
          name: 'Special case',
          link: `/shipments/${this.shipment.id}/declarations/special-case-ens`,
          class: 'text-warning',
          count: statistics?.specialCase?.ens ?? 0,
        },
      ]
      if (!this.showAllENSStatuses) {
        statsRows = statsRows.filter(x => x.count > 0)
      }
      return statsRows
    },
    submittedEnsCount() {
      return this.ENSItems.reduce((acc, item) => acc + item.count, 0)
    },
    hasNoNotSentParcels() {
      const { statistics } = this.$attrs
      return (statistics?.notSent?.ens ?? 0) === 0
    },
    getTooltipMsg() {
      if (this.manifestHasIssues) {
        return 'Fix parcel issues before submitting ENS filings'
      }
      return ''
    },
  },
  methods: {
    sendEns(messageType, requestType) {
      this.requestPending = true
      this.$http.post(`/v1/shipments/${this.shipment.id}/ensDeclarationsFull`, {
        messageType: messageType,
        declarationType: requestType,
      })
        .then(response => {
          EventBus.$emit('refresh-statistics')
          this.requestPending = false
          this.ensSubmitted = true
          // Display success message from backend response
          if (response.data && response.data.message) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: response.data.message,
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
          }
        })
        .catch(error => {
          handleError(error, this.$toast)
          this.requestPending = false
        })
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
